/**
 * 공통코드 읽기
 * ---------------------------------------------------------------------------
 * 상태·구분 값의 표시명과 색은 서버의 공통코드(config.code_group/code_item)가
 * 정한다. 화면에 배열로 박아 두면 같은 목록이 파일마다 조금씩 달라지고,
 * 운영자가 표시명 하나를 바꾸려 해도 배포를 기다려야 한다.
 *
 * 쓰는 법
 *
 *   const codes = useCodes(['AUDIT_RESULT', 'ALERT_SEVERITY']);
 *   codes.label('AUDIT_RESULT', row.result)      // '권한 거부'
 *   codes.color('AUDIT_RESULT', row.result)      // 'volcano'
 *   codes.options('ALERT_SEVERITY')              // Select 용 [{value,label}]
 *   <CodeTag codes={codes} group="AUDIT_RESULT" value={row.result} />
 *
 * 아직 안 왔거나 없는 코드면 **코드값 자체**를 보여 준다. 빈칸으로 두면
 * 화면이 고장난 것처럼 보이고, 무엇이 안 왔는지도 알 수 없다.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'antd';
import { apiClient, type NormalizedError } from './api-client';

export interface CodeItem {
  itemCode: string;
  itemName: string;
  itemValue: string | null;
  parentItemCode: string | null;
  extraAttrs: Record<string, unknown>;
  sortOrder: number;
  isSystem: boolean;
}

export type CodeMap = Record<string, CodeItem[]>;

export interface Codes {
  /** 원본. 특수한 경우에만 직접 본다. */
  map: CodeMap;
  loading: boolean;
  label: (group: string, code: string | null | undefined) => string;
  color: (group: string, code: string | null | undefined) => string | undefined;
  value: (group: string, code: string | null | undefined) => string | null;
  items: (group: string) => CodeItem[];
  options: (group: string) => Array<{ value: string; label: string }>;
}

/**
 * 여러 그룹을 한 번에 읽는다.
 *
 * 그룹 목록은 화면마다 고정이라 정렬해서 질의 키로 쓴다. 순서만 다른 같은
 * 목록이 두 번 오가지 않게 한다.
 */
export function useCodes(groups: string[]): Codes {
  const key = useMemo(() => [...groups].sort(), [groups.join(',')]);

  const query = useQuery<CodeMap, NormalizedError>({
    queryKey: ['codes', key],
    enabled: key.length > 0,
    // 코드값은 거의 바뀌지 않는다. 화면을 옮길 때마다 다시 받을 이유가 없다.
    staleTime: 10 * 60 * 1000,
    queryFn: async () =>
      (await apiClient.get<CodeMap>('/config/codes', { params: { groups: key.join(',') } })).data,
  });

  const map = query.data ?? {};

  const find = (group: string, code: string | null | undefined) =>
    code ? map[group]?.find((i) => i.itemCode === code) : undefined;

  return {
    map,
    loading: query.isLoading,
    label: (group, code) => find(group, code)?.itemName ?? (code ?? ''),
    color: (group, code) => find(group, code)?.extraAttrs?.color as string | undefined,
    value: (group, code) => find(group, code)?.itemValue ?? null,
    items: (group) => map[group] ?? [],
    options: (group) =>
      (map[group] ?? []).map((i) => ({ value: i.itemCode, label: i.itemName })),
  };
}

/** 코드 한 칸을 태그로. 색도 공통코드가 정한다. */
export function CodeTag({
  codes,
  group,
  value,
  fallback = '—',
}: {
  codes: Codes;
  group: string;
  value: string | null | undefined;
  fallback?: string;
}) {
  if (!value) return <>{fallback}</>;
  return <Tag color={codes.color(group, value)}>{codes.label(group, value)}</Tag>;
}
